import { TextButton, validators } from '@approvalmax/ui';
import { State } from 'modules/data';
import { ResetPasswordLinkIcon } from 'modules/sprites';
import React, { FC, memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { routingService } from 'services/routing';
import { getPath, Path } from 'urlBuilder';

import { resetPassword } from '../../actions';
import { PasswordResetModuleData } from '../../reducers/page/moduleReducer';
import { getAuthCommand } from '../../selectors/commandSelectors';
import { getModule } from '../../selectors/pageSelectors';
import { messages } from './PasswordResetModule.messages';
import {
    BottomBlock,
    Card,
    CardForm,
    CentralBlock,
    Container,
    InvisibleSubmit,
    SignupButton,
    StyledTextEditor,
    SuccessComment,
    SuccessIcon,
    Title,
    ValidationMessage,
} from './PasswordResetModule.styles';
import { PasswordResetModuleProps } from './PasswordResetModule.types';

const PasswordResetModule: FC<PasswordResetModuleProps> = memo((props) => {
    const { onBlink } = props;

    const emailInputRef = useRef<HTMLInputElement>(null);

    const authCommand = useSelector((state: State) => getAuthCommand(state));
    const module = useSelector((state: State) => getModule<PasswordResetModuleData>(state));

    const [loginText, setLoginText] = useState(module.email || '');
    const [loginTouched, setLoginTouched] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setTimeout(() => {
            if (emailInputRef.current) {
                emailInputRef.current.focus();
            }
        }, 20);
    }, []);

    const showLogin = () => {
        onBlink &&
            onBlink(() => {
                routingService.replace(getPath(Path.login, { email: getNormalisedEmail() }));
            });
    };

    const validateEmail = () => {
        let emailValidationError;

        const email = getNormalisedEmail();

        if (validators.required(email)) {
            emailValidationError = messages.emailRequired;
        } else if (validators.email(email)) {
            emailValidationError = messages.emailInvalid;
        }

        return emailValidationError;
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        reset();
    };

    const onChangeLogin = (value: string) => {
        setLoginText(value.trim());
    };

    const reset = () => {
        setLoginTouched(true);

        if (validateEmail()) {
            return;
        }

        dispatch(resetPassword(getNormalisedEmail()));
    };

    const getNormalisedEmail = () => {
        return loginText.trim();
    };

    const renderSuccessBlock = () => {
        return (
            <Container>
                <Card>
                    <Title>{messages.titleSuccessText}</Title>

                    <SuccessIcon>
                        <ResetPasswordLinkIcon width={90} height={102} />
                    </SuccessIcon>

                    <SuccessComment>
                        {messages.successComment({
                            email: <b>{getNormalisedEmail()}</b>,
                        })}
                    </SuccessComment>
                </Card>
            </Container>
        );
    };

    const renderPasswordResetBlock = () => {
        let emailValidationError;

        if (loginTouched) {
            emailValidationError = validateEmail();
        }

        return (
            <Container>
                <CardForm onSubmit={onSubmit}>
                    <InvisibleSubmit type='submit' />

                    <Title>{messages.titleText}</Title>

                    <CentralBlock>
                        <StyledTextEditor
                            value={loginText}
                            ref={emailInputRef}
                            type='text'
                            name='email'
                            autoComplete='on'
                            spellCheck={false}
                            onChange={onChangeLogin}
                            theme='auth'
                            placeholder={emailValidationError ? undefined : messages.emailPlaceholder}
                            invalid={Boolean(emailValidationError)}
                        />

                        {emailValidationError && <ValidationMessage>{emailValidationError}</ValidationMessage>}

                        <SignupButton padding='narrow20' execute={reset} command={authCommand}>
                            {messages.resetButtonText}
                        </SignupButton>
                    </CentralBlock>
                </CardForm>

                <BottomBlock>
                    <TextButton size='normal-plus' colorTheme='green' command={authCommand} execute={showLogin}>
                        {messages.loginNowButtonText}
                    </TextButton>
                </BottomBlock>
            </Container>
        );
    };

    if (module.success) {
        return renderSuccessBlock();
    } else {
        return renderPasswordResetBlock();
    }
});

export default PasswordResetModule;
